import { useMemo } from 'react';

import { VITALS_ACTIVITY_TYPE, CHART_FILTERS_TYPE } from '../constants';
import {
  convertValueDistanceToMiles,
  formatHeightValueWithMetric,
  formatValueWeightWithMetric,
  makeDateForAverageOrTotal,
  makeTickForData,
  numberWithCommas,
} from '../utils';
import { COLORS } from '../../../constants/styles';

const { DAY } = CHART_FILTERS_TYPE;

const {
  HEART_RATE,
  SATURATION,
  WEIGHT,
  BMI,
  STEPS,
  DISTANCE,
  HEIGHT,
  BLOOD_GLUCOSE,
} = VITALS_ACTIVITY_TYPE;

export function useAverageData(
  formatMessage,
  data,
  dataType,
  filterType,
  singleDayMode,
  isWeightInKg,
  isHeightInMeters,
  isMilligramsPerDeciliter,
) {
  const {
    averageOrTotal: { value, showAverageLine, dateForSingleMode },
  } = data;
  const isDayTypeFilter = filterType === DAY.type;
  const isBarTypeChart = [STEPS, DISTANCE].includes(dataType);
  const getMetricFromDataType = () => {
    switch (dataType) {
      case HEIGHT:
        return {
          metric: isHeightInMeters
            ? formatMessage({
                id: 'patient.healthboard.height.metric.cm',
              })
            : '',
          formatValue: formatHeightValueWithMetric(value, isHeightInMeters),
        };
      case WEIGHT:
        return {
          metric: isWeightInKg
            ? formatMessage({
                id: 'patient.healthboard.weight.metric.kg',
              })
            : formatMessage({
                id: 'patient.healthboard.weight.metric',
              }),
          formatValue: formatValueWeightWithMetric(value, isWeightInKg),
        };
      case BMI:
        return {
          metric: '',
          formatValue: value && value.toFixed(1),
        };
      case DISTANCE:
        return {
          metric: formatMessage({ id: 'patient.healthboard.distance.metric' }),
          formatValue: convertValueDistanceToMiles(value),
        };
      case STEPS:
        return {
          metric: formatMessage({ id: 'patient.healthboard.steps.metric' }),
          formatValue: value && numberWithCommas(value.toFixed()),
        };
      case SATURATION:
        return {
          metric: formatMessage({
            id: 'patient.healthboard.saturation.metric',
          }),
          formatValue: value,
        };
      case HEART_RATE:
        return {
          metric: formatMessage({
            id: 'patient.healthboard.heart_rate.metric',
          }),
          formatValue: value,
        };
      case BLOOD_GLUCOSE:
        return {
          metric: isMilligramsPerDeciliter
            ? formatMessage({
                id:
                  'patient.healthboard.blood_glucose.metric.milligrams_per_deciliter',
              })
            : formatMessage({
                id:
                  'patient.healthboard.blood_glucose.metric.millimoles_per_liter',
              }),
          formatValue: value,
        };
    }
  };

  const makeAverageLineEntity = useMemo(
    () => () => {
      if (!value) return {};
      return {
        annotation: {
          events: ['click'],
          annotations: [
            ...(filterType !== DAY.type &&
              showAverageLine && [
                {
                  drawTime: 'afterDatasetsDraw',
                  id: 'hline',
                  type: 'line',
                  mode: 'horizontal',
                  scaleID: 'right-y-axis',
                  value,
                  borderColor: '#00b1e1',
                  borderWidth: 1,
                  label: {
                    backgroundColor: 'white',
                    position: 'right',
                    content: makeTickForData(
                      value,
                      dataType,
                      true,
                      isWeightInKg,
                    ),
                    fontColor: COLORS.grayDark,
                    fontSize: 11,
                    fontFamily: 'Helvetica',
                    fontStyle: 'bold',
                    yAdjust: -8,
                    cornerRadius: 1,
                    xPadding: 2,
                    yPadding: 1,
                    enabled: true,
                  },
                },
              ]),
            ...(isBarTypeChart && [
              {
                drawTime: 'afterDatasetsDraw',
                id: 'baseline',
                type: 'line',
                mode: 'horizontal',
                scaleID: 'right-y-axis',
                value: 0,
                borderColor: '#e5e5e5',
                borderWidth: 1,
              },
            ]),
          ],
        },
      };
    },
    [
      data,
      dataType,
      filterType,
      showAverageLine,
      isWeightInKg,
      isHeightInMeters,
      isBarTypeChart,
    ],
  );

  return {
    ...getMetricFromDataType(),
    title:
      isDayTypeFilter && ![BMI, WEIGHT, HEIGHT].includes(dataType)
        ? formatMessage({ id: 'patient.healthboard.total' })
        : formatMessage({ id: 'patient.healthboard.average' }),
    date: singleDayMode
      ? dateForSingleMode
      : makeDateForAverageOrTotal(data, filterType),
    averageLine: makeAverageLineEntity(),
  };
}



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/Chart/useAverageData.jsx